import type { AllowedCountry } from '@repo/shared-config/app/constants/zendesk';
import { ZENDESK_DEFAULTS, ZENDESK_KEYS } from '@repo/shared-config/app/constants/zendesk';
import { getEnvironmentVariables } from '@repo/shared-config/app/helper/environment-variables';
import { isNil } from 'es-toolkit';
import { loadScript } from '#utils/load-script';
import { COUNTRY_TO_HELP_CENTER_MAP } from '#zendesk/url-composer/constants';

const ENV = getEnvironmentVariables();

const isMounted = () => !isNil(window.zE);

const _zendeskIframes = new Set<HTMLIFrameElement>();
let _iframeObserver: MutationObserver | null = null;

const _startObservingIframes = () => {
  _iframeObserver = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      for (const node of Array.from(mutation.addedNodes)) {
        if (node instanceof HTMLIFrameElement) {
          _zendeskIframes.add(node);
        }
      }
    }
  });
  _iframeObserver.observe(document.body, { childList: true });
};

const shouldMountSandBox = ENV.deployTarget !== 'production';

const _setZendeskLocale = (locale: string) => {
  if (isMounted()) {
    window.zE?.('messenger:set', 'locale', locale);
  }
};

const _loadZendesk = async (orgCountry: AllowedCountry) => {
  let zendeskKey: string;

  if (shouldMountSandBox) {
    zendeskKey = ZENDESK_KEYS.__sandbox__;
  } else {
    zendeskKey =
      orgCountry in ZENDESK_KEYS
        ? ZENDESK_KEYS[orgCountry as keyof typeof ZENDESK_KEYS] // Cast is safe here as we checked the country is in the ZENDESK_KEYS object
        : ZENDESK_KEYS[ZENDESK_DEFAULTS.UNAUTH_FALLBACK_COUNTRY];
  }

  window.zESettings = {
    messenger: {
      canUserSeeConversationList: false,
    },
  };

  _startObservingIframes();
  try {
    await loadScript(`https://static.zdassets.com/ekr/snippet.js?key=${zendeskKey}`, {
      scriptId: 'ze-snippet',
    });
  } catch {
    // we ignore the error, as Zendesk is not critical for our app to work, and we don't want to flag the error on Sentry
  }
};

const _openZendesk = () => {
  if (isMounted()) {
    window.zE?.('messenger', 'open');
  }
};

// We delay opening the widget to give time for the redirections to finish and the page to load a bit
const _delayedOpen = () => {
  setTimeout(() => {
    _openZendesk();
    window.removeEventListener('ze-loaded', _delayedOpen);
  }, 1500);
};

export const openZendeskAfterMounting = () => {
  if (isMounted()) {
    _delayedOpen();
  } else {
    window.addEventListener('ze-loaded', _delayedOpen);
  }
};

export const closeZendesk = () => {
  if (isMounted()) {
    window.zE?.('messenger', 'close');
  }
};

export const showZendesk = () => {
  if (isMounted()) {
    window.zE?.('messenger', 'show');
  }
};

export const hideZendesk = () => {
  if (isMounted()) {
    window.zE?.('messenger', 'hide');
  }
};

export const loadAndAuthenticateZendesk = async ({
  orgCountry,
  userLocale,
}: {
  orgCountry: string;
  userLocale: string;
}) => {
  try {
    const orgCountryToUse =
      orgCountry.toLowerCase() in COUNTRY_TO_HELP_CENTER_MAP
        ? COUNTRY_TO_HELP_CENTER_MAP[
            orgCountry.toLowerCase() as keyof typeof COUNTRY_TO_HELP_CENTER_MAP
          ]
        : ZENDESK_DEFAULTS.UNAUTH_FALLBACK_COUNTRY;
    ZENDESK_DEFAULTS.UNAUTH_FALLBACK_COUNTRY;
    await _loadZendesk(orgCountryToUse);
    _setZendeskLocale(userLocale);
    _iframeObserver?.disconnect();
    _iframeObserver = null;
    window.dispatchEvent(new Event('ze-loaded'));
  } catch (error) {
    _iframeObserver?.disconnect();
    _iframeObserver = null;
    // eslint-disable-next-line no-console -- Done via a console.error for now as there is no clear Sentry contract yet
    console.error('Error loading and authenticating the Zendesk widget:', error);
    // this.errorHandler('Error loading and authenticating the Zendesk widget:', error);
  }
};

export const unmountZendesk = () => {
  _iframeObserver?.disconnect();
  _iframeObserver = null;

  if (isMounted()) {
    window.zE?.('messenger', 'hide');
    window.zE?.('messenger', 'logoutUser');
  }

  window.zE = undefined;
  window.zEmbed = undefined;
  window.zEACLoaded = undefined;
  window.zESettings = undefined;

  document.getElementById('ze-snippet')?.remove();

  for (const iframe of _zendeskIframes) {
    iframe.parentNode?.removeChild(iframe);
  }
  _zendeskIframes.clear();
};

declare global {
  interface Window {
    zE: ((command: string, ...args: unknown[]) => void) | undefined;
    zEmbed: ((command: string, ...args: unknown[]) => void) | undefined;
    zEACLoaded: ((command: string, ...args: unknown[]) => void) | undefined;
    zESettings: Record<string, unknown> | undefined;
  }
}
